// src/components/Layout.js
import React, { useState, useRef, useEffect, useCallback, useMemo, memo, createContext, useContext } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
  LayoutGrid, BookOpen, GitBranch, TrendingUp,
  CalendarDays, Settings as SettingsIcon, X, Check,
  ChevronLeft, ChevronRight, Loader2,
  Trophy, Users, Heart, Lock,
  Megaphone, FileText, BarChart2,
} from 'lucide-react';
import { useScreenSize }   from '@/hooks/ui/useScreenSize';
import { useTheme }        from '@/contexts/ThemeContext';
import { useAuth }         from '@/contexts/AuthContext';
import { useProfile }      from '@/contexts/ProfileContext';
// FIX #13: useLocalStorage alias removed — all Layout subscriptions now go through
// useSyncStore (IDB + Firestore) so prayer data is consistent with every other
// page and survives a sign-out/sign-in cycle correctly.
import { useSyncStore, flushPendingSyncWrites } from '@/hooks/sync/useSyncStore';
import {
  STORE_DAILY_ENTRIES, STORE_CALENDAR_EVENTS, STORE_CHALLENGES,
  STORE_PRAYER_REQUESTS, STORE_PRAYER_CHECKINS,
  STORE_SERMON_NOTES, STORE_PEOPLE_CONTACTS, STORE_EXPENSES,
  STORE_DISCIPLESHIP_ATTENDANCE, STORE_BIBLE_STUDY_PROGRESS,
  STORE_HABIT_FLAGS,
  STORE_DISCIPLESHIP_DATA, STORE_COMPLETED_CHALLENGES,
  STORE_BIBLE_STUDY_ATTENDANCE, STORE_TITHES_OFFERING, STORE_OUTGOING_SUPPORT,
  STORE_ONE_TIME_GIFTS, STORE_BUDGET_SETTINGS,
  STORE_WEEKLY_REPORTS, STORE_MONTHLY_REPORTS,
  STORE_BIBLE_BOOKMARKS, STORE_BIBLE_HIGHLIGHTS, STORE_BIBLE_NOTES,
  STORE_BIBLE_HISTORY, STORE_BIBLE_FONT_SIZE, STORE_BIBLE_RED_LETTER,
  STORE_BIBLE_SHOW_DAILY, STORE_SUPPORT_RECEIVED, STORE_SAVED_PDFS,
  STORE_FEATURE_UNLOCK_LEVEL, STORE_BIBLE_READING_HISTORY,
  STORE_USER_PROFILE,
} from '@/lib/storeKeys';
import { format, addDays, subDays, addYears } from 'date-fns';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { CHURCH_PLANT_START_DATE }        from '@/config/app.constants';
import { idbGet }                         from '@/lib/db/storageAdapter';
import { useFeatureUnlock, getLevelTaskProgress, getLevelTasks, getLevel5Tasks, verifyMasterCode } from '@/hooks/feature/useFeatureUnlock';
import { formatDate as fmtDateLocal } from '@/utils/helpers';
import { useGlobalChallengeSync } from '@/hooks/sync/useGlobalChallengeSync';
import { useAutoChallengeProgress, consecutiveRun } from '@/hooks/sync/useAutoChallengeProgress';
import { usePrayerInboxImport } from '@/hooks/sync/usePrayerInboxImport';
import { usePrayerAcks } from '@/hooks/sync/usePrayerAcks';
import { useSharedPrayerSync } from '@/hooks/sync/useSharedPrayerSync';
import OfflineBanner    from '@/components/feedback/OfflineBanner';
import SyncStatusBadge  from '@/components/feedback/SyncStatusBadge';
import UpdateBanner     from '@/components/feedback/UpdateBanner';
import { isEntryFilled } from '@/utils/journalEntryStatus';
import { lockScroll, unlockScroll } from '@/components/ui/dialog';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';

// Wrapper for isEntryFilled - adapts argument order to JournalEntry's signature
const checkDayFilled = (entry, dateKey, challenges, prayerRequests, prayedDays) => {
  return isEntryFilled(entry, prayerRequests, dateKey, prayedDays, [], challenges, []);
};

// ── Constants ────────────────────────────────────────────────────
// ── Layout constants — iOS gets tighter values ────────────────────────────
// IMPORTANT: data-platform is set by App.js at runtime (after module evaluation),
// so we must read it lazily — never at module parse time. Using a function
// that reads the attribute on first call (during React render) guarantees
// App.js has already written the attribute before Layout ever renders.
const getIsIOS = () => document.documentElement.getAttribute('data-platform') === 'ios';
// These are derived once per render via the getter, not at module load time.
// Computed in the Layout component body (see below) so they are always fresh.
// Kept as module-level fallbacks for sub-components that need a static value
// after the first render — by the time any sub-component mounts, App.js will
// have written data-platform, so a single lazy read here is safe.
const IS_IOS = (() => getIsIOS())();   // evaluated on first import (post App.js setup)

// Module-level fallback: use IS_IOS for height decisions
// CSS @supports (display-mode: standalone) provides PWA-specific overrides

// ── Scroll lock for modals ─────────────────────────────────────
// lockScroll/unlockScroll now imported from @/components/ui/dialog — ProfileModal
// shares the same openCount/savedScrollY bookkeeping as every Radix Dialog instead
// of maintaining its own separate body.style.cssText lock. Two independent lock
// systems were stomping each other's state when a Dialog and ProfileModal
// overlapped, leaving body permanently pinned (position: fixed) with nothing
// left in the app that still believed it needed to unlock it.
const NAV_H  = IS_IOS ? 54 : 64;      // iOS: 54px  Android/desktop: 64px
const BAR_H  = IS_IOS ? 36 : 42;      // iOS: 36px  Android/desktop: 42px
const TOP_HEADER_H = IS_IOS ? 40 : 48; // iOS: 40px  Android/desktop: 48px
const ROOT_PAGES = ['/', '/journal', '/calendar', '/discipleship', '/prayer', '/growth'];

const CUSTOM_BACK = {
  '/stewardship/people':              '/stewardship',
  '/stewardship/expenses':            '/stewardship',
  '/stewardship/reports':             '/stewardship',
  '/discipleship/bible-study-guide':  '/discipleship',
};

// RULE: every key here MUST be imported from storeKeys.js — no bare strings.
// A typo in a bare string would silently export null for that key with no error.
const BACKUP_KEYS = [
  // ── Core journal & contacts ──────────────────────────────────
  STORE_DAILY_ENTRIES, STORE_PEOPLE_CONTACTS, STORE_CALENDAR_EVENTS,
  // ── Stewardship ──────────────────────────────────────────────
  STORE_EXPENSES, STORE_WEEKLY_REPORTS, STORE_MONTHLY_REPORTS,
  // 'giftList' is a legacy alias for 'oneTimeGifts' — kept for backwards-compatible restore
  STORE_SUPPORT_RECEIVED, STORE_OUTGOING_SUPPORT, STORE_ONE_TIME_GIFTS, 'giftList',
  STORE_TITHES_OFFERING, STORE_BUDGET_SETTINGS,
  // ── Discipleship ─────────────────────────────────────────────
  STORE_BIBLE_STUDY_PROGRESS, STORE_DISCIPLESHIP_DATA, STORE_DISCIPLESHIP_ATTENDANCE,
  STORE_BIBLE_STUDY_ATTENDANCE,
  // ── Prayer ───────────────────────────────────────────────────
  STORE_PRAYER_REQUESTS, STORE_PRAYER_CHECKINS,
  // ── Sermons & documents ──────────────────────────────────────
  STORE_SERMON_NOTES, STORE_SAVED_PDFS,
  // ── Growth & challenges ───────────────────────────────────────
  STORE_CHALLENGES, STORE_COMPLETED_CHALLENGES,
  // ── Feature unlock state ─────────────────────────────────────
  // Unlock level + completed-chapter ledger (both are real stores and must survive a restore)
  STORE_FEATURE_UNLOCK_LEVEL, STORE_BIBLE_READING_HISTORY,
  // ── Bible reader ─────────────────────────────────────────────
  STORE_BIBLE_BOOKMARKS, STORE_BIBLE_HIGHLIGHTS, STORE_BIBLE_NOTES,
  STORE_BIBLE_HISTORY, STORE_BIBLE_FONT_SIZE, STORE_BIBLE_RED_LETTER,
  STORE_BIBLE_SHOW_DAILY,
  // ── Profile ──────────────────────────────────────────────────
  STORE_USER_PROFILE,
  // NOTE: familyDevotionLog, focusModeLog, focusModeSession are in IDB
  // but excluded here pending confirmation that those features are live.
  // Add them here and to EXPORT_KEYS in Settings.js when ready.
];

// triggerAutoExport: exported for use by Settings → "Manual Backup" button.
// Previously this fired automatically on every back-press to a root page,
// running Promise.all over 40+ idbGet() calls on every navigation.
// That was removed (Layout C2): auto-backup on nav is confusing UX and
// burns unnecessary IDB reads. Users trigger backups explicitly in Settings.
export const triggerAutoExport = async () => {
  try {
    const backup = { _version: 2, _exportedAt: new Date().toISOString() };
    await Promise.all(
      BACKUP_KEYS.map(async (key) => {
        let value = await idbGet(key);
        if (value === null) {
          try { const raw = localStorage.getItem(key); value = raw ? JSON.parse(raw) : null; } catch { value = null; }
        }
        backup[key] = value;
      })
    );
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `sheepheard-backup-${new Date().toISOString().slice(0,10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  } catch (err) {
    console.error('[Layout] Export failed:', err);
    throw err; // re-throw so callers can show an error toast
  }
};

// ═══════════════════════════════════════════════════════
//  AVATAR COMPONENT (lines 133)
//  Profile picture display with upload state
// ═══════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────
const Avatar = ({ size = 36, photo = '', onClick, clickable = false, uploading = false }) => {
  const img = photo
    ? <img src={photo} alt="profile" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
    : <img src="/icons/icon-512.png" alt="Sheepheard" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />;
  if (clickable && onClick) {
    return (
      <button onClick={onClick} title="Change profile picture"
        style={{ display: 'inline-flex', borderRadius: '50%', overflow: 'hidden', cursor: uploading ? 'wait' : 'pointer', border: 'none', padding: 0, background: 'none', flexShrink: 0, position: 'relative' }}>
        {img}
        {uploading && (
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 style={{ width: size * 0.4, height: size * 0.4, color: '#fff', animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
          </span>
        )}
      </button>
    );
  }
  return img;
};

// ── useAvatarProfile hook ────────────────────────────────────────
// FIX: Restored after accidental deletion - manages profile photo/name from userProfile store
const useAvatarProfile = () => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useSyncStore(STORE_USER_PROFILE, {});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!user) return;
    setUserProfile(prev => {
      const next = { ...prev };
      let changed = false;
      if (user.displayName && !prev.displayName) { next.displayName = user.displayName; changed = true; }
      if (user.photoURL && !prev.photoURL) { next.photoURL = user.photoURL; changed = true; }
      return changed ? next : prev;
    });
  }, [user]);

  const name = userProfile?.displayName || user?.displayName || '';
  const photo = userProfile?.photoURL || user?.photoURL || '';

  const saveName = async (n) => {
    setUserProfile(prev => ({ ...prev, displayName: n }));
    if (user) {
      try { const { updateProfile } = await import('firebase/auth'); await updateProfile(user, { displayName: n }); }
      catch { console.warn('[useAvatarProfile] updateProfile failed:', 'Profile update not available'); }
    }
  };

  const savePhoto = async (photoUrl) => {
    setUserProfile(prev => ({ ...prev, photoURL: photoUrl }));
    if (user) {
      try { const { updateProfile } = await import('firebase/auth'); await updateProfile(user, { photoURL: photoUrl }); }
      catch { console.warn('[useAvatarProfile] updateProfile failed:', 'Profile update not available'); }
    }
  };

  return { name, photo, saveName, savePhoto, uploadingPhoto, setUploadingPhoto };
};

// ── CalendarErrorBoundary ─────────────────────────────────────────
class CalendarErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: false }; }
  static getDerivedStateFromError() { return { error: true }; }
  render() {
    if (this.state.error) return <div className="p-4 text-center text-sm text-stone-500">Unable to load calendar. Please refresh.</div>;
    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════════════════
//  SECTION: NAVIGATION CONFIG (lines 295-352)
//  Navigation arrays & buildNavItems helper
// ═══════════════════════════════════════════════════════════════
// ── Nav definitions ───────────────────────────────────────────────
const UNIVERSAL_NAV = [
  { to: '/journal',      icon: BookOpen,     label: 'Journal'    },
  { to: '/discipleship', icon: GitBranch,    label: 'Disciples'  },
  { to: '/prayer',       icon: Heart,        label: 'Prayer'     },
  { to: '/people',       icon: Users,        label: 'People'     },
  { to: '/growth',       icon: Trophy,       label: 'Growth Hub' },
  { to: '/calendar',     icon: CalendarDays, label: 'Calendar'   },
];

const ALL_DRAWER_NAV = [
  { group: 'Core', items: [
    { to: '/',             icon: LayoutGrid,  label: 'Dashboard'   },
    { to: '/journal',      icon: BookOpen,    label: 'Journal'     },
    { to: '/prayer',       icon: Heart,       label: 'Prayer'      },
    { to: '/bible',        icon: BookOpen,    label: 'Bible'       },
    { to: '/calendar',     icon: CalendarDays,label: 'Calendar'    },
  ]},
  { group: 'Disciples', items: [
    { to: '/discipleship', icon: GitBranch,   label: 'Discipleship' },
    { to: '/discipleship/bible-study-guide', icon: BookOpen, label: 'Bible Study Guide' },
    { to: '/evangelism',   icon: Megaphone,   label: 'Evangelism'  },
    { to: '/people',       icon: Users,       label: 'People'      },
  ]},
  { group: 'Growth', items: [
    { to: '/growth',       icon: Trophy,      label: 'Growth Hub'  },
    { to: '/sermon-notes', icon: FileText,    label: 'Sermon Notes'},
  ]},
  { group: 'Stewardship', items: [
    { to: '/stewardship',          icon: TrendingUp, label: 'Stewardsheep'   },
    { to: '/stewardship/expenses', icon: BarChart2,  label: 'Budget Ledger'  },
    { to: '/stewardship/reports',  icon: FileText,   label: 'Reports'        },
    { to: '/stewardship/people',   icon: Users,      label: 'People Keeper' },
  ]},
];

const ROLE_EXTRA_NAV = {
  planter:       [{ to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep' }],
  pastor:        [{ to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep' }, { to: '/sermon-notes', icon: BookOpen, label: 'Sermons' }],
  elder:         [{ to: '/sermon-notes', icon: BookOpen, label: 'Sermons' }],
  deacon:        [{ to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep' }],
  widowedPastor: [{ to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep' }, { to: '/sermon-notes', icon: BookOpen, label: 'Sermons' }],
  missionary:    [{ to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep' }],
  professional:  [{ to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep' }],
  worshipLeader: [{ to: '/sermon-notes', icon: BookOpen, label: 'Sermons' }],
};

const buildNavItems = (roleId) => {
  const base   = [{ to: '/', icon: LayoutGrid, label: 'Dashboard' }, ...UNIVERSAL_NAV];
  const extras = ROLE_EXTRA_NAV[roleId] ?? [];
  const seen   = new Set(base.map(i => i.to));
  const merged = [...base];
  extras.forEach(e => { if (!seen.has(e.to)) merged.push(e); });
  return merged;
};

const MOBILE_NAV_ITEMS = [
  { to: '/stewardship', icon: TrendingUp, label: 'Stewardsheep', useLogo: false },
  { to: '/journal',     icon: BookOpen,   label: 'Journal',      useLogo: false },
  { to: '/',            icon: LayoutGrid, label: 'Home',         useLogo: true  },
  { to: '/discipleship',icon: GitBranch,  label: 'Disciples',    useLogo: false },
  { to: '/growth',      icon: Trophy,     label: 'Growth',       useLogo: false },
];

const AppLogoIcon = ({ isActive }) => (
  <div style={{
    width: IS_IOS ? 40 : 50, height: IS_IOS ? 40 : 50, borderRadius: '50%', overflow: 'hidden',
    boxShadow: isActive
      ? `0 0 0 ${IS_IOS ? '2.5px' : '3px'} #c9a84c,0 4px 14px rgba(201,168,76,.45)`
      : `0 0 0 ${IS_IOS ? '2.5px' : '3px'} rgba(255,255,255,.9),0 4px 12px rgba(0,0,0,.22)`,
    background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'box-shadow .15s',
  }}>
    <img src="/icons/icon-512.png" alt="Dashboard" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
  </div>
);

// ── FilledDaysContext (for calendar dot indicators) ────────────────
const FilledDaysContext = createContext({});

const JournalDayContent = memo(({ date }) => {
  const filledDays = useContext(FilledDaysContext);
  const key        = format(date, 'yyyy-MM-dd');
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {date.getDate()}
      {filledDays[key] && (
        <span style={{ position: 'absolute', top: -5, right: -8, width: 8, height: 8, borderRadius: '50%', background: '#16a34a', boxShadow: '0 0 0 1.5px white', pointerEvents: 'none' }} />
      )}
    </span>
  );
});
JournalDayContent.displayName = 'JournalDayContent';
const JOURNAL_CAL_COMPONENTS = { DayContent: JournalDayContent };

// ── LevelBadge ────────────────────────────────────────────────────
const LevelBadge = ({ level, levelDef, progress, nextLevel, onClick }) => (
  <button onClick={onClick}
    className="flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all active:scale-95 hover:opacity-80"
    style={{ borderColor: levelDef?.color + '55', background: levelDef?.color + '18' }}
    title={nextLevel ? `Next: ${nextLevel.label} — ${progress?.hint}` : 'Fully unlocked!'}>
    <span style={{ fontSize: 12 }}>{levelDef?.emoji}</span>
    <span style={{ fontSize: 10, fontWeight: 700, color: levelDef?.color }}>Lv.{level}</span>
    {nextLevel && (
      <div className="w-10 h-1 rounded-full bg-stone-200 dark:bg-stone-600 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${progress?.pct ?? 0}%`, background: levelDef?.color }} />
      </div>
    )}
  </button>
);

// ── MasteryCodeInput ──────────────────────────────────────────────
const MasteryCodeInput = ({ targetLevel, targetDef, onUnlocked }) => {
  const [code,   setCode]   = useState('');
  const [status, setStatus] = useState('idle');
  const { unlockRoute }     = useFeatureUnlock();

  const handleSubmit = async () => {
    const trimmed = code.trim(); if (!trimmed) return;
    setStatus('checking');
    try {
      const valid = await verifyMasterCode(trimmed);
      if (valid) {
        // Pass the level number directly — unlockRoute sets storedLevel to
        // max(current, targetLevel) so effectiveLevel reflects the new tier.
        unlockRoute(targetLevel);
        setStatus('success'); setCode('');
        toast.success(`${targetDef?.emoji ?? '🔓'} Level ${targetLevel} features unlocked`, { description: targetDef?.celebrationSub, duration: 5000 });
        setTimeout(() => { setStatus('idle'); onUnlocked?.(); }, 1200);
      } else { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); }
    } catch { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); }
  };

  return (
    <div className="flex gap-2 items-center" onClick={e => e.stopPropagation()}>
      <input type="password" value={code}
        onChange={e => { setCode(e.target.value); setStatus('idle'); }}
        onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        placeholder="Enter Mastery Code…"
        style={{ fontSize: '16px' }}
        className={`flex-1 min-w-0 px-2 py-1 rounded-lg border outline-none transition-colors bg-white dark:bg-stone-700 dark:text-stone-100 text-xs ${
          status === 'error' ? 'border-red-400' : status === 'success' ? 'border-green-500' : 'border-stone-200 dark:border-stone-600 focus:border-forest-500'}`}
      />
      <button onClick={handleSubmit} disabled={!code.trim() || status === 'checking'}
        className="shrink-0 min-w-[52px] px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-green-700 hover:bg-green-800 dark:bg-green-600 dark:hover:bg-green-500 disabled:opacity-40 active:scale-95 transition-all">
        {status === 'checking' ? '…' : status === 'success' ? '✓' : status === 'error' ? '✗' : 'Apply'}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════
//  SECTION: NAV DRAWER (lines 459-517)
//  Mobile full-screen navigation menu
// ═══════════════════════════════════════════════════════
// ── NavDrawer (mobile full menu) ─────────────────────────────────
// NOTE: No window.history manipulation — just React state open/close.
const NavDrawer = ({ open, onClose, onLevelClick }) => {
  const { isUnlocked } = useFeatureUnlock();
  const navigate       = useNavigate();

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (to) => { navigate(to); onClose(); };

  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white dark:bg-stone-900 rounded-t-3xl shadow-2xl overflow-hidden"
        style={{ maxHeight: '85dvh' }} onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-stone-300 dark:bg-stone-600" />
        </div>
        <div className="flex items-center justify-between px-5 pb-2 border-b border-stone-100 dark:border-stone-800">
          <p className="font-serif font-bold text-base text-stone-900 dark:text-stone-100">All Pages</p>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-800 text-stone-500"><X className="w-4 h-4"/></button>
        </div>
        <div className="overflow-y-auto pb-6" style={{ maxHeight: 'calc(85dvh - 80px)' }}>
          {ALL_DRAWER_NAV.map(({ group, items }) => {
            const visible = items.filter(i => isUnlocked(i.to));
            const locked  = items.filter(i => !isUnlocked(i.to));
            if (!visible.length && !locked.length) return null;
            return (
              <div key={group} className="px-4 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 px-1">{group}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {visible.map(({ to, icon: Icon, label }) => (
                    <button key={to} onClick={() => go(to)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-stone-50 dark:bg-stone-800 hover:bg-forest-50 dark:hover:bg-forest-900/30 text-stone-700 dark:text-stone-300 transition-colors text-sm font-medium text-left active:scale-95">
                      <Icon className="w-4 h-4 shrink-0 text-stone-400" strokeWidth={1.5} />
                      <span className="truncate">{label}</span>
                    </button>
                  ))}
                  {locked.map(({ to, icon: Icon, label }) => (
                    <button key={to} onClick={onLevelClick}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-stone-50/60 dark:bg-stone-800/60 text-stone-300 dark:text-stone-600 text-sm font-medium text-left relative opacity-60">
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                      <span className="truncate">{label}</span>
                      <Lock className="w-3 h-3 absolute top-1.5 right-1.5" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="px-4 pt-3">
            <button onClick={() => go('/settings')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 text-sm font-semibold hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors active:scale-95">
              <SettingsIcon className="w-4 h-4 text-stone-400" /> Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── BottomNav ─────────────────────────────────────────────────────
const BottomNav = ({ onLevelClick }) => {
  const { isUnlocked } = useFeatureUnlock();
  const navHeight = IS_IOS ? 'h-[54px]' : 'h-16';
  const gapClass = IS_IOS ? 'gap-0.5' : 'gap-1';
  const maxWidth = IS_IOS ? 'max-w-[64px]' : 'max-w-[60px] sm:max-w-[80px]';
  const iconSize = IS_IOS ? 'w-5 h-5' : 'w-5 h-5 sm:w-6 sm:h-6';
  const textSize = IS_IOS ? 'text-[9px]' : 'text-[9px] sm:text-[10px]';

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/90 dark:bg-stone-900/90 backdrop-blur-xl border-t border-stone-200 dark:border-stone-700"
      data-testid="bottom-navigation"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', transform: 'translateZ(0)', willChange: 'transform' }}>
      <div
        className={`${navHeight} flex items-center justify-around px-1 max-w-2xl mx-auto`}
      >
        {MOBILE_NAV_ITEMS.map(({ to, icon: Icon, label, useLogo }) => {
          const navUnlock = isUnlocked(to);
          if (!navUnlock) {
            return (
              <button key={to} onClick={onLevelClick}
                className={`flex flex-col items-center ${gapClass} transition-colors active:scale-90 flex-1 ${maxWidth} px-2 py-1 rounded-xl text-stone-300 dark:text-stone-600 relative`}>
                <Icon className={iconSize} strokeWidth={2} />
                <span className={`${textSize} font-medium leading-tight`}>{label}</span>
                <Lock className="w-2.5 h-2.5 absolute top-0.5 right-2" />
              </button>
            );
          }
          return (
            <NavLink key={to} to={to} end={to === '/'}
              className={({ isActive }) =>
                `flex flex-col items-center ${gapClass} transition-colors active:scale-90 flex-1 ${maxWidth} px-2 py-1 rounded-xl ${
                  useLogo ? '' : isActive ? 'text-forest-500 dark:text-forest-400' : 'text-stone-400 dark:text-stone-500 hover:text-stone-600'
                }`}
            >
              {({ isActive }) => (
                <>
                  {useLogo
                    ? <div style={{ marginBottom: -2, transform: 'translateY(-2px)' }}><AppLogoIcon isActive={isActive} /></div>
                    : <Icon className={iconSize} strokeWidth={isActive ? 2.5 : 2} />
                  }
                  <span className={`${textSize} font-medium leading-tight ${useLogo && !isActive ? 'text-stone-400' : ''}`}>{label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

// ── JournalDateBar ────────────────────────────────────────────────
const JournalDateBar = ({ journalDate, setJournalDate, pickerOpen, setPickerOpen, filledDays = {}, streakCount = 0, isTablet = false, scrollContainerRef = null }) => {
  const [barVisible, setBarVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking     = useRef(false);

  const startDate       = CHURCH_PLANT_START_DATE instanceof Date ? CHURCH_PLANT_START_DATE : new Date(CHURCH_PLANT_START_DATE);
  const ministryEndDate = addYears(startDate, 6);
  const isToday         = format(journalDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
  const selectedFilled  = filledDays[format(journalDate, 'yyyy-MM-dd')];

  useEffect(() => {
    // Listens on scrollContainerRef (mobile's <main>, see Layout.jsx) when
    // provided; falls back to window for tablet, which still uses normal
    // document-level scroll.
    const target = scrollContainerRef?.current || window;
    const getScrollY = () => (scrollContainerRef?.current ? scrollContainerRef.current.scrollTop : window.scrollY);
    const onScroll = () => {
      if (pickerOpen) return;
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(() => {
        const y = getScrollY();
        if (y < lastScrollY.current || y <= 10) setBarVisible(true);
        else if (y > lastScrollY.current && y > 60) setBarVisible(false);
        lastScrollY.current = y; ticking.current = false;
      });
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, [pickerOpen, scrollContainerRef]);

  useEffect(() => { if (pickerOpen) setBarVisible(true); }, [pickerOpen]);

  const barBottom = barVisible
    ? `calc(${NAV_H}px + env(safe-area-inset-bottom,0px))`
    : `calc(${NAV_H - BAR_H}px + env(safe-area-inset-bottom,0px))`;
  const calBottom = `calc(${NAV_H + BAR_H}px + env(safe-area-inset-bottom,0px) + .25rem)`;

  return (
    <FilledDaysContext.Provider value={filledDays}>
      <>
        {pickerOpen && (
          <>
            <div className="fixed inset-0 z-[55]" onClick={() => setPickerOpen(false)} />
            <div className={`fixed z-[60] right-2 bg-white dark:bg-stone-800 rounded-2xl shadow-2xl border border-stone-200 dark:border-stone-700 ${isTablet ? 'left-[calc(14rem+0.5rem)]' : 'left-2'}`} style={{ bottom: calBottom }}>
              <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <div className="flex items-center gap-2">
                  <p className="font-serif font-semibold text-stone-800 dark:text-stone-100 text-sm">Select a Date</p>
                  <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300 text-[11px] font-bold">
                    {streakCount > 0 ? `🔥 ${streakCount}-day streak` : 'No streak yet'}
                  </span>
                </div>
                <button onClick={() => setPickerOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-400 transition-colors"><X className="w-4 h-4"/></button>
              </div>
              <div className="w-full">
                <style>{`
                  .full-cal{width:100%}.full-cal .rdp{margin:0;width:100%}.full-cal .rdp-months{width:100%;display:block}
                  .full-cal .rdp-month{width:100%;display:block}.full-cal .rdp-table{width:100%;border-collapse:collapse;table-layout:fixed}
                  .full-cal .rdp-head_row{display:grid;grid-template-columns:repeat(7,1fr);width:100%}
                  .full-cal .rdp-row{display:grid;grid-template-columns:repeat(7,1fr);width:100%}
                  .full-cal .rdp-head_cell{text-align:center;padding:8px 2px;font-size:.7rem;width:100%}
                  .full-cal .rdp-cell{display:flex;justify-content:center;align-items:center;padding:4px 0;width:100%}
                  .full-cal .rdp-day{width:100%;max-width:44px;aspect-ratio:1;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.875rem;margin:0 auto}
                  .full-cal .rdp-tbody{display:block;width:100%}
                  .full-cal [class*="rdp-day_selected"]{background-color:#166534!important;color:white!important;border-radius:50%!important}
                  .full-cal [class*="rdp-day_today"]:not([class*="rdp-day_selected"]){font-weight:bold;color:#166534}
                `}</style>
                <div className="full-cal px-2 pb-2">
                  <CalendarErrorBoundary>
                    <CalendarComponent
                      mode="single" selected={journalDate}
                      onSelect={(d) => { if (d) { setJournalDate(d); setPickerOpen(false); } }}
                      defaultMonth={journalDate} className="w-full" components={JOURNAL_CAL_COMPONENTS}
                    />
                  </CalendarErrorBoundary>
                </div>
              </div>
              <div className="px-4 py-2.5 border-t border-stone-100 dark:border-stone-700 bg-stone-50 dark:bg-stone-800/60 rounded-b-2xl space-y-1.5">
                <div className="flex items-center justify-center gap-1.5">
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block', boxShadow: '0 0 0 1.5px white,0 0 0 2.5px #16a34a', flexShrink: 0 }} />
                  <span className="text-[10px] text-stone-400 dark:text-stone-500">= journal filled that day</span>
                </div>
                <p className="text-[11px] text-stone-400 text-center">6-Year Journal · {format(startDate, 'MMM yyyy')} – {format(ministryEndDate, 'MMM yyyy')}</p>
              </div>
            </div>
          </>
        )}
        <div className={`fixed right-0 z-[45] transition-all duration-300 ${isTablet ? 'left-56' : 'left-0'} ${!barVisible ? 'pointer-events-none' : ''}`} style={{ bottom: barBottom, transform: 'translateZ(0)', willChange: 'transform' }}>
          <div className="flex items-center justify-between px-4 py-2 bg-forest-500 text-white">
            <button onClick={() => setJournalDate(d => subDays(d, 1))} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"><ChevronLeft className="w-5 h-5"/></button>
            <button onClick={() => setPickerOpen(v => !v)} className="flex items-center gap-2 px-3 py-1 rounded-full hover:bg-white/20 transition-colors">
              <CalendarDays className="w-4 h-4"/>
              <span className="text-sm font-semibold">{isToday ? 'Today · ' : ''}{format(journalDate, 'MMM d, yyyy')} ({format(journalDate, 'EEE')})</span>
              {selectedFilled && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#86efac', display: 'inline-block', flexShrink: 0 }} />}
            </button>
            <button onClick={() => setJournalDate(d => addDays(d, 1))} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"><ChevronRight className="w-5 h-5"/></button>
          </div>
        </div>
      </>
    </FilledDaysContext.Provider>
  );
};

// ── SideNav (tablet ≥768px) ───────────────────────────────────────
const SideNav = ({ onProfileOpen, photo, onLevelClick }) => {
  const { role } = useProfile();
  const items    = useMemo(() => buildNavItems(role), [role]);
  const { isUnlocked, level: currentLevelSide, levelDef: currentLevelDefSide, effectiveLevel, effectiveLevelDef, progress, nextLevel } = useFeatureUnlock();

  return (
    <aside className="fixed top-0 left-0 h-full w-56 bg-white/95 dark:bg-stone-900/95 backdrop-blur-xl border-r border-stone-200 dark:border-stone-700 flex flex-col z-50 shadow-lg">
      <div className="flex items-center px-5 py-4 border-b border-stone-100 dark:border-stone-800">
        <button onClick={onProfileOpen} className="flex items-center gap-2 min-w-0 hover:opacity-70 active:scale-95 transition-all">
          <Avatar size={28} photo={photo} />
          <p className="font-serif font-bold text-stone-900 dark:text-stone-100 shrink-0 whitespace-nowrap" style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.9375rem)' }}>Sheepheard</p>
        </button>
      </div>
      <div className="mx-3 mt-2 mb-1 px-3 py-1.5 rounded-xl bg-stone-50 dark:bg-stone-800 flex items-center gap-2 justify-between">
        {/* FIX: show currentLevel (earned), not effectiveLevel (may include master-unlock bonus) */}
        <LevelBadge level={currentLevelSide} levelDef={currentLevelDefSide} progress={progress} nextLevel={nextLevel} onClick={onLevelClick} />
      </div>
      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {items.map(({ to, icon: Icon, label }) => (
          isUnlocked(to) ? (
            <NavLink key={to} to={to} end={to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium ${
                  isActive ? 'bg-forest-500 text-white shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-900'
                }`}>
              {({ isActive }) => <><Icon className="w-5 h-5 flex-shrink-0" strokeWidth={isActive ? 2 : 1.5} /><span>{label}</span></>}
            </NavLink>
          ) : (
            <button key={to} onClick={onLevelClick}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-stone-300 dark:text-stone-600 hover:bg-stone-50 dark:hover:bg-stone-800 active:scale-95 transition-all w-full text-left">
              <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              <span className="flex-1">{label}</span>
              <Lock className="w-3.5 h-3.5 shrink-0" />
            </button>
          )
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-stone-100 dark:border-stone-800">
        <p className="text-[10px] text-stone-400 text-center">Discipleship Companion · 2 Tim 2:2</p>
      </div>
    </aside>
  );
};

// ── ProfileModal ──────────────────────────────────────────────────
const ProfileModal = ({ open, onClose, avatarProfile, onLevelClick }) => {
  const [editingName, setEditingName] = useState(false);
  const [nameInput,   setNameInput]   = useState('');
  const { user }       = useAuth();
  const { profile, getRoleData, getLifeStageData } = useProfile();
  const navigate       = useNavigate();
  const fileRef        = useRef();
  const { isUnlocked } = useFeatureUnlock();
  const { name, photo, saveName, savePhoto, uploadingPhoto } = avatarProfile;
  const roleData      = getRoleData();
  const lifeStageData = getLifeStageData();

  useEffect(() => {
    if (!open) return;
    lockScroll();
    return () => unlockScroll();
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleNameSave = async () => { await saveName(nameInput.trim()); setEditingName(false); };
  const handlePhoto    = (e) => { const f = e.target.files?.[0]; if (!f) return; e.target.value = ''; savePhoto(f); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center px-4"
      style={{ paddingTop: `calc(${IS_IOS ? '2.5rem' : '3rem'} + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 0.5rem)` }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-stone-800 rounded-3xl shadow-2xl border border-stone-100 dark:border-stone-700 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 9rem - var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))' }}>
        <div className="flex items-center justify-between px-8 pt-8 pb-2">
          <p className="font-serif text-2xl font-bold text-stone-900 dark:text-stone-100">My Profile</p>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-700 text-stone-500 hover:text-stone-700 transition-colors"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex flex-col items-center gap-0 px-8 pt-6 pb-4">
          {/* 1 — Avatar */}
          <Avatar size={112} photo={photo} clickable uploading={uploadingPhoto} onClick={() => !uploadingPhoto && fileRef.current?.click()} />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          {uploadingPhoto && <p className="text-xs text-forest-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin"/> Syncing…</p>}

          {/* 2 — Name (tap to edit) */}
          <div className="mt-3 w-full">
            {editingName ? (
              <div className="flex items-center gap-2 w-full">
                <input autoFocus value={nameInput} onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
                  placeholder="Your name…" className="flex-1 text-base px-4 py-2.5 rounded-xl border border-stone-200 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100 outline-none focus:border-forest-500" />
                <button onClick={handleNameSave} className="w-9 h-9 rounded-full bg-forest-500 flex items-center justify-center hover:bg-forest-700"><Check className="w-4 h-4 text-white"/></button>
                <button onClick={() => setEditingName(false)} className="w-9 h-9 rounded-full bg-stone-200 dark:bg-stone-600 flex items-center justify-center hover:bg-stone-300"><X className="w-4 h-4 text-stone-600"/></button>
              </div>
            ) : (
              <button onClick={() => { setNameInput(name); setEditingName(true); }} className="w-full text-center hover:opacity-70 transition-opacity">
                <p className="font-semibold text-stone-900 dark:text-stone-100 text-xl">{name || 'Tap to set your name'}</p>
              </button>
            )}
          </div>

          {/* 3 — Email */}
          {user?.email && <p className="text-xs text-stone-400 mt-1.5">{user.email}</p>}

          {/* 4 — Role · Life Stage pill */}
          <div className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100 dark:bg-stone-700">
            <span className="text-base">{roleData.emoji}</span>
            <span className="text-xs font-bold text-stone-700 dark:text-stone-300">{roleData.label}</span>
            <span className="text-stone-300 dark:text-stone-600">·</span>
            <span className="text-xs text-stone-500 dark:text-stone-400">{lifeStageData.emoji} {lifeStageData.label}</span>
          </div>
          {profile?.ministryName && <p className="text-xs text-stone-400 mt-1">{profile.ministryName}</p>}
        </div>
        <div className="border-t border-stone-100 dark:border-stone-700">
          {/* Settings now lives in the Core group below */}
          <div className="pb-4">
            {ALL_DRAWER_NAV.map(({ group, items }) => (
              <div key={group} className="px-5 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 px-1">{group}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {items.map(({ to, icon: Icon, label }) => isUnlocked(to) ? (
                    <button key={to} onClick={() => { navigate(to); onClose(); }}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-stone-50 dark:bg-stone-700/50 hover:bg-forest-50 dark:hover:bg-forest-900/30 text-stone-700 dark:text-stone-300 text-sm font-medium text-left transition-colors">
                      <Icon className="w-4 h-4 shrink-0 text-stone-400" strokeWidth={1.5} />
                      <span className="truncate">{label}</span>
                    </button>
                  ) : (
                    <button key={to} onClick={onLevelClick}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-stone-50/60 dark:bg-stone-700/30 text-stone-300 dark:text-stone-600 text-sm font-medium text-left relative">
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                      <span className="truncate">{label}</span>
                      <Lock className="w-3 h-3 absolute top-1.5 right-1.5" />
                    </button>
                  ))}
                  {group === 'Core' && (
                    <button onClick={() => { navigate('/settings'); onClose(); }}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-stone-50 dark:bg-stone-700/50 hover:bg-forest-50 dark:hover:bg-forest-900/30 text-stone-700 dark:text-stone-300 text-sm font-medium text-left transition-colors">
                      <SettingsIcon className="w-4 h-4 shrink-0 text-stone-400" strokeWidth={1.5} />
                      <span className="truncate">Settings</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── calcChallengeStreak ───────────────────────────────────────────
// Local streak counter for challenge checkIns arrays.
// Used by LevelPanel's Lv.5 "ongoing challenges" display.
// Mirrors calcStreak in GrowthHub without importing from there.
const calcChallengeStreak = (checkIns = []) => {
  if (!checkIns.length) return 0;
  const sorted = [...new Set(checkIns)].sort().reverse();
  const now  = new Date();
  const pad  = (n) => String(n).padStart(2, '0');
  const fmt  = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const today = fmt(now);
  const yest  = fmt(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  if (sorted[0] !== today && sorted[0] !== yest) return 0;
  let streak = 1;
  for (let i = 1; i < sorted.length; i++) {
    const diff = Math.round((new Date(sorted[i-1]) - new Date(sorted[i])) / 86400000);
    if (diff === 1) streak++; else break;
  }
  return streak;
};

// ═══════════════════════════════════════════════════════
//  SECTION: LEVEL PANEL (lines 814-1310)
//  Journey level modal with milestone progress
// ═══════════════════════════════════════════════════════
// ─────────────────────────────────────────────────────────
const LevelPanel = ({ open, onClose }) => {
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [masteryLevel,  setMasteryLevel]  = useState(null); // shown only on long-press
  const longPressTimer  = useRef(null);
  const longPressFired  = useRef(false);
  const navigate        = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { level: currentLevel, effectiveLevel, effectiveLevelDef, progress, nextLevel, isUnlocked: isRouteUnlocked, UNLOCK_LEVELS: levels } = useFeatureUnlock();

  // ── Level panel data (completed / active challenges, role) ────────────────
  // These stores are already open elsewhere in Layout — no extra Firestore reads.
  const [lpCompletedChallenges]  = useSyncStore(STORE_COMPLETED_CHALLENGES,   []);
  const [lpActiveChallenges]     = useSyncStore(STORE_CHALLENGES,             []);
  const { role: lpRole } = useProfile();

  // useFeatureUnlock does not expose earnedTypes; challenge-based progress needs the completed list
  const earnedTypes = lpCompletedChallenges;

  const handleLongPressStart = useCallback((lvlNum) => {
    if (lvlNum < 0) return; // negative levels don't exist — Level 0 (Seeker) IS interactive
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setMasteryLevel(prev => prev === lvlNum ? null : lvlNum);
      if (navigator.vibrate) navigator.vibrate(40);
    }, 600);
  }, []);

  const handleLongPressCancel = useCallback(() => {
    clearTimeout(longPressTimer.current);
  }, []);

  const handleLevelClick = useCallback((lvlNum) => {
    if (lvlNum < 0) return; // negative levels don't exist — Level 0 (Seeker) IS expandable
    if (longPressFired.current) { longPressFired.current = false; return; }
    setExpandedLevel(prev => prev === lvlNum ? null : lvlNum);
    setMasteryLevel(null); // hide mastery when collapsing/switching
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4" onClick={() => { onClose(); setExpandedLevel(null); }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white dark:bg-stone-800 rounded-3xl shadow-2xl flex flex-col"
        style={{ maxHeight: '88dvh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 pt-5 pb-3 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="font-serif font-bold text-lg text-stone-900 dark:text-stone-100">Your Journey Level</p>
              {/* FIX: show currentLevel (earned level), not effectiveLevel */}
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-xs font-bold shrink-0" style={{ background: levels[currentLevel]?.color ?? '#737373' }}>
                {levels[currentLevel]?.emoji} Lv.{currentLevel}
              </span>
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-400">Complete the challenges for each level to unlock the next stage of your journey.</p>
          </div>
          <button onClick={() => { onClose(); setExpandedLevel(null); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 dark:bg-stone-700 text-stone-500 ml-3">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 space-y-2 pb-4">
          {(!levels || typeof Object.entries(levels).map !== 'function') ? (
            <p className="text-sm text-stone-500 text-center py-4">Loading levels...</p>
          ) : Object.entries(levels).map(([lvl, def]) => {
            const lvlNum = parseInt(lvl);
            const features = def.features ?? [];
            // isEarned: mastery-unlocked levels also count as earned so features/styles reflect it.
            const isEarned  = effectiveLevel >= lvlNum;
            // isCurrent: organic earned level — drives task-list / progress-bar logic only.
            const isCurrent = currentLevel === lvlNum;
            // isDisplayCurrent: highest unlocked level — drives CURRENT badge + card highlight.
            const isDisplayCurrent = effectiveLevel === lvlNum;
            // isMasteryUnlocked = earned via code, not organic progression.
            const isMasteryUnlocked = effectiveLevel >= lvlNum && currentLevel < lvlNum;
            const isLocked  = effectiveLevel < lvlNum;
            const isExpanded = expandedLevel === lvlNum;

            return (
              <div key={lvl}
                className={`rounded-2xl border transition-all ${
                  isDisplayCurrent ? 'border-2 bg-white dark:bg-stone-800 shadow-md cursor-pointer hover:shadow-lg'
                  : isEarned       ? 'border-2 cursor-pointer hover:shadow-sm'
                  : isExpanded     ? 'border-2 border-amber-400 bg-amber-50/60 dark:bg-amber-900/10 cursor-pointer'
                  : 'border border-dashed border-stone-300 dark:border-stone-600 bg-stone-50 dark:bg-stone-800/40 opacity-40 cursor-pointer hover:opacity-60'
                }`}
                style={
                  isDisplayCurrent ? { borderColor: def.color }
                  : isEarned       ? { borderColor: def.color, background: def.color + '12' }
                  : {}
                }
                onPointerDown={() => handleLongPressStart(lvlNum)}
                onPointerUp={handleLongPressCancel}
                onPointerLeave={handleLongPressCancel}
                onPointerCancel={handleLongPressCancel}
                onClick={() => handleLevelClick(lvlNum)}>
                <div className="flex items-start gap-3 p-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-lg shrink-0 ${isEarned ? 'shadow-sm' : ''}`}
                    style={{ background: isEarned ? def.color + '30' : isExpanded ? '#fef3c7' : '#e7e5e480' }}>
                    {isEarned ? def.emoji : isExpanded ? '🔑' : '🔒'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-stone-900 dark:text-stone-100" style={isEarned && !isDisplayCurrent ? { color: def.color } : {}}>
                        Lv.{lvl} — {def.label}
                      </p>
                      {isDisplayCurrent && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: def.color }}>CURRENT</span>
                      )}
                      {isEarned && !isDisplayCurrent && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: def.color }}>
                          ✓ Level achieved — <span className="italic">by His grace!</span>{isMasteryUnlocked ? ' 🔑' : ''}
                        </span>
                      )}
                    </div>
                    <p className={`text-[11px] mt-0.5 ${isEarned ? 'text-stone-500 dark:text-stone-400' : 'text-stone-400 dark:text-stone-500'}`}>
                      {features.includes('*') ? 'All features unlocked' : features.map(f => f.replace(/^\//, '').replace(/\//g,' › ').replace(/-/g,' ')).join(', ')}
                    </p>
                    {isCurrent && nextLevel && effectiveLevel === currentLevel && (() => {
                      const tp = getLevelTaskProgress(lvlNum, earnedTypes);
                      return (
                        <div className="mt-1.5">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className="text-[10px] text-stone-400">Progress to Lv.{lvlNum + 1}</p>
                            <div className="flex items-center gap-1.5">
                              <span
                                className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full text-white"
                                style={{ background: def.color }}>
                                {tp.met.length}/{tp.met.length + tp.unmet.length}
                              </span>
                              <p className="text-[10px] font-bold" style={{ color: def.color }}>{progress.pct}%</p>
                            </div>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-stone-200 dark:bg-stone-600 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${progress.pct}%`, background: def.color }} />
                          </div>
                          <p className="text-[10px] text-stone-400 mt-0.5 italic">{progress.hint}</p>
                        </div>
                      );
                    })()}
                  </div>
                  {/* All levels are expandable — each shows the requirements for that level */}
                  {true && (
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 mt-1 transition-transform ${isEarned ? 'text-stone-400 dark:text-stone-500' : 'text-stone-300 dark:text-stone-600'} ${isExpanded ? 'rotate-90' : ''}`} />
                  )}
                </div>

                {/* Expanded: requirements list + optional mastery code */}
                {/* Current level shows tasks needed to advance; locked levels show their own requirements */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-stone-100 dark:border-stone-700" onClick={e => e.stopPropagation()}>

                    {/* Gate task list:
                        - isCurrent (at this level now): always show the task list → user needs direction
                        - isMasteryUnlocked: show confirmation (tasks already accomplished by code)
                        - isEarned but not current (past level): show scripture/desc confirmation
                        - isLocked (not yet reached): show requirements to unlock this level */}
                    {isCurrent ? (
                      /* ── Current level: show tasks needed to reach next level ── */
                      /* ── Level 5 (Sheepheard / max): show cycling challenges ── */
                      lvlNum >= 5 ? (
                        <div className="pt-2 space-y-2">
                          <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-1">
                            Next 5 Challenges to Complete
                          </p>
                          {(() => {
                            const cyclingTasks = getLevel5Tasks(lpCompletedChallenges, lpActiveChallenges, lpRole ?? 'planter');
                            if (cyclingTasks.length === 0) {
                              return (
                                <div className="text-center py-3">
                                  <p className="text-[11px] text-stone-500 dark:text-stone-400 italic">
                                    All challenges completed! You are a true Sheepheard. 👑
                                  </p>
                                </div>
                              );
                            }
                            return cyclingTasks.map((t) => {
                              const isActive = t.active;
                              const activeChallenge = isActive
                                ? (lpActiveChallenges || []).find(c => c.templateId === t.key)
                                : null;
                              const daysDone = activeChallenge ? (activeChallenge.checkIns ?? []).length : 0;
                              const daysTotal = t.days || 30;
                              const pct = activeChallenge ? Math.min(Math.round((daysDone / daysTotal) * 100), 100) : 0;

                              return (
                                <div key={t.id}
                                  className="flex items-center gap-2 rounded-lg px-1 py-0.5 cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                                  onClick={() => { navigate('/growth', { state: { tab: 'challenges' } }); onClose(); setExpandedLevel(null); }}
                                >
                                  <span style={{ fontSize: 14 }}>{isActive ? '🔥' : '☆'}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[11px] text-stone-700 dark:text-stone-300 font-semibold leading-tight truncate">{t.label}</p>
                                    {isActive && activeChallenge ? (
                                      <div className="flex items-center gap-1.5 mt-0.5">
                                        <div className="flex-1 h-1 rounded-full bg-stone-200 dark:bg-stone-600 overflow-hidden">
                                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: def.color }} />
                                        </div>
                                        <span className="text-[9px] font-bold tabular-nums shrink-0" style={{ color: def.color }}>{daysDone}/{daysTotal}d</span>
                                      </div>
                                    ) : (
                                      <p className="text-[9px] text-stone-400 mt-0.5">Tap to start · {daysTotal}d</p>
                                    )}
                                  </div>
                                  <ChevronRight className="w-3 h-3 text-stone-300 dark:text-stone-600 shrink-0" />
                                </div>
                              );
                            });
                          })()}
                          <div
                            className="flex items-center justify-center gap-1 mt-1 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => { navigate('/growth', { state: { tab: 'challenges' } }); onClose(); setExpandedLevel(null); }}
                          >
                            <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: def.color }}>View all in Growth Hub</span>
                            <ChevronRight className="w-2.5 h-2.5" style={{ color: def.color }} />
                          </div>
                        </div>
                      ) : nextLevel ? (
                        <>
                          {(() => {
                            const tp = getLevelTaskProgress(lvlNum, earnedTypes);
                            return (
                              <div className="flex items-center justify-between mt-2 mb-1.5">
                                <p className="text-[9px] font-bold uppercase tracking-widest text-stone-400 dark:text-stone-500">
                                  Tasks to accomplish · Progress to Lv.{lvlNum + 1}
                                </p>
                                <span
                                  className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full text-white shrink-0"
                                  style={{ background: def.color }}>
                                  {tp.met.length}/{tp.met.length + tp.unmet.length}
                                </span>
                              </div>
                            );
                          })()}
                          <div className="space-y-1 mb-2">
                            {getLevelTasks(lvlNum, lpCompletedChallenges).map(t => {
                              // All challenge tasks now route to /growth
                              const tappable = !t.done;
                              const handleClick = () => {
                                if (!tappable) return;
                                navigate('/growth', { state: { tab: 'challenges' } });
                                onClose();
                                setExpandedLevel(null);
                              };
                              return (
                                <div key={t.id}
                                  onClick={handleClick}
                                  className={`flex items-center gap-2 rounded-lg px-1 py-0.5 transition-colors ${tappable ? 'cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800' : ''}`}>
                                  {t.done ? (
                                    <span className="shrink-0 leading-none" style={{ fontSize: 12 }}>⭐</span>
                                  ) : (
                                    <span className="shrink-0 leading-none" style={{ fontSize: 16, opacity: 0.35, color: '#a8a29e' }}>☆</span>
                                  )}
                                  <span
                                    className={`flex-1 leading-tight ${t.done ? 'line-through text-stone-400 dark:text-stone-500' : 'text-stone-600 dark:text-stone-400'}`}
                                    style={{ fontSize: 'clamp(10px, 2.8vw, 14px)' }}
                                  >
                                    {t.label}
                                  </span>
                                  {!t.done && t.days && (
                                    <span className="text-[9px] font-bold text-stone-400 shrink-0">{t.days}d</span>
                                  )}
                                  {t.done && (
                                    <span className="text-[8px] font-extrabold uppercase tracking-wide text-forest-500 dark:text-forest-400 shrink-0">✓</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        /* At max level (nextLevel is null) but lvlNum < 5 — show scripture */
                        <div className="flex flex-col gap-0 pt-0.5">
                          <p className="text-[10px] text-stone-400 dark:text-stone-500 leading-relaxed">{def.scriptureText} — {def.scripture}</p>
                          <p className="text-[10px] text-stone-400 dark:text-stone-500 leading-relaxed italic">{def.desc}</p>
                        </div>
                      )
                    ) : isEarned ? (
                      /* ── Past earned level OR mastery-unlocked: show scripture/desc confirmation ── */
                      <div className="flex flex-col gap-0 pt-0.5">
                        {isMasteryUnlocked && (
                          <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-0.5">
                            🔑 Unlocked via Mastery Code — all tasks accomplished.
                          </p>
                        )}
                        <p className="text-[10px] text-stone-400 dark:text-stone-500 leading-relaxed">
                          {def.scriptureText} — {def.scripture}
                        </p>
                        <p className="text-[10px] text-stone-400 dark:text-stone-500 leading-relaxed italic">
                          {def.desc}
                        </p>
                      </div>
                    ) : (
                      /* ── Not yet earned: show requirements to reach THIS level ── */
                      <>
                        {(() => {
                          const reqs = def.requirements || [];
                          const totalReqs = reqs.length;

                          // Level 5 is the FINAL level — never say "progress to Lv.6".
                          // UNLOCK_LEVELS[5].requirements are discipleship milestones, not
                          // advancement gates (the progression loop only checks levels 0–4).
                          // At Lv.5 the user sees cycling challenges (CHALLENGE_LIBRARY),
                          // not these milestone types — so don't preview them here.
                          if (lvlNum >= 5) {
                            return (
                              <div className="pt-2 pb-1">
                                <p className="text-[10px] text-stone-500 dark:text-stone-400 italic leading-relaxed">
                                  {lvlNum === currentLevel + 1
                                    ? <>Complete the tasks shown in your current level above to unlock {def.emoji} {def.label} — the <strong>final level</strong>.</>
                                    : <>🏁 <strong>Final Level</strong> — all app features are unlocked. Progress through Levels 0–4 to reach this stage and access ongoing cycling challenges in Growth Hub.</>
                                  }
                                </p>
                                <p className="text-[10px] text-stone-400 dark:text-stone-500 mt-1">{def.scriptureText} — {def.scripture}</p>
                              </div>
                            );
                          }

                          if (totalReqs === 0) {
                            return (
                              <div className="pt-1">
                                <p className="text-[10px] text-stone-400 dark:text-stone-500 italic">{def.scriptureText} — {def.scripture}</p>
                              </div>
                            );
                          }

                          return null;
                        })()}
                        {/* Task list: only for locked levels 0–4. Level 5 is the final level —
                            its UNLOCK_LEVELS[5].requirements are milestone types not used as
                            advancement gates, so we don't show them as a locked preview. */}
                        {isLocked && masteryLevel === lvlNum && (
                          <>
                            <p className="text-[9px] font-bold uppercase tracking-widest text-amber-600 mb-1 mt-2">🔑 Mastery Code</p>
                            <MasteryCodeInput targetLevel={lvlNum} targetDef={def} onUnlocked={() => { setExpandedLevel(null); setMasteryLevel(null); onClose(); }} />
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-stone-100 dark:border-stone-700 shrink-0">
          <p className="text-[10px] text-stone-400 text-center italic">
            {currentLevel >= 5
              ? 'Tap your level card to see your ongoing challenges.'
              : 'Tap your current level to see what\'s needed to advance.'}
          </p>
        </div>
      </div>
    </div>
  );
};  

// ── Global challenge sync ─────────────────────────────────────────
// Extracted to src/hooks/sync/useGlobalChallengeSync.js
// Imported above — useGlobalChallengeSync() is called in Layout below.

// ── Layout (main) ─────────────────────────────────────────────────
const Layout = () => {
  const { isTablet }      = useScreenSize();
  const { isForestTheme, changeFontSize, changeUiScale, changeLineSpacing, changeButtonScale } = useTheme();
  const location          = useLocation();
  const navigate          = useNavigate();
  const isJournalPage     = location.pathname === '/journal';
  const mainScrollRef     = useRef(null);

  // Mobile's <main> is the app's real scroll container (see index.css'
  // html/body lock) so overscroll-behavior can be applied to a *nested*
  // scroller instead of the document root — Chromium (both the native
  // Android WebView and the installed PWA) demotes document-root scrolling
  // to the main thread when overscroll-behavior is set there directly,
  // which is what made scrolling feel "sticky" in earlier attempts.
  // Tablet still uses normal document/window scroll (its <main> just grows
  // with content), so this is scoped off there via data-scroll-shell.
  useEffect(() => {
    document.documentElement.setAttribute('data-scroll-shell', isTablet ? 'false' : 'true');
  }, [isTablet]);

  // ── Custom pull-to-refresh ──────────────────────────────────────────────
  // Mobile only. Chrome's native pull-to-refresh needs the *document* itself
  // to be the scrollable element — since the mobile shell above pins html/
  // body and moves real scrolling into <main>, the browser's native gesture
  // has nothing to attach to anymore and silently stops working (this is
  // what broke after the scroll-shell commit). Reimplementing it here on
  // <main> restores the same behavior: pull down past a threshold while
  // already at the top → reload, same as the native gesture did.
  const [pullDistance, setPullDistance] = useState(0);
  const pullDistanceRef = useRef(0);
  useEffect(() => {
    if (isTablet) return; // tablet's <main> isn't the scroller — native document PTR still applies there
    const el = mainScrollRef.current;
    if (!el) return;

    const PULL_THRESHOLD = 70;
    const PULL_MAX = 100;
    let startY = null;
    let startX = null;

    const onTouchStart = (e) => {
      startY = null;
      startX = null;
      if (window.__dialogOpenCount > 0) return; // same guard dialog.jsx already relies on
      if (el.scrollTop > 0) return;
      for (let node = e.target; node && node !== el; node = node.parentElement) {
        if (node.scrollTop > 0) return; // touch began inside a nested scroller that is scrolled down
      }
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
    };
    const onTouchMove = (e) => {
      if (startY === null) return;
      const delta = e.touches[0].clientY - startY;
      const deltaX = Math.abs(e.touches[0].clientX - startX);
      if (delta <= 0 || el.scrollTop > 0 || deltaX > delta) {
        startY = null;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }
      const next = Math.min(delta * 0.5, PULL_MAX);
      pullDistanceRef.current = next;
      setPullDistance(next);
    };
    const onTouchEnd = () => {
      if (startY === null) return;
      startY = null;
      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        // FIX FLUSH-2: reload() begins tearing down the page immediately —
        // too fast for a pending debounced IDB/Firestore write to land on its
        // own. Flush and await it first (capped so an offline/slow write
        // can't hang the refresh indefinitely), then reload.
        Promise.race([
          flushPendingSyncWrites(),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]).finally(() => window.location.reload());
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    const onTouchCancel = () => {
      startY = null;
      startX = null;
      pullDistanceRef.current = 0;
      setPullDistance(0);
    };
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [isTablet]);

  const avatarProfile = useAvatarProfile();

  // Status bar icon color must match this page's background, not the OS
  // dark-mode default. Forest theme = dark backgrounds → light icons.
  // Light theme = light backgrounds → dark icons.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    SystemBars.setStyle({ style: isForestTheme ? SystemBarsStyle.Light : SystemBarsStyle.Dark });
  }, [isForestTheme]);

  // ── Elderly-friendly defaults (one-time on first render) ────────────────
  // If user's life season is senior/seniorActiveMinistry and they haven't
  // explicitly set display preferences yet, auto-apply large font, large
  // buttons, relaxed spacing, comfortable density.
  useEffect(() => {
    // LEGACY: One-time elderly UI defaults (safe to remove after June 2026)
  // This applies comfortable/large defaults for senior users on first load.
  // After 3 releases, users who skip updates will re-apply on next load — acceptable.
  const alreadySet = localStorage.getItem('ds_elderly_defaults_applied');
    if (alreadySet) return;
    (async () => {
      try {
        const profile = await idbGet(STORE_USER_PROFILE);
        const season = profile?.lifeSeason || profile?.life_season;
        if (season === 'senior' || season === 'seniorActiveMinistry') {
          // Only apply if user hasn't manually changed these preferences
          if (!localStorage.getItem('ds_ui_scale'))    { changeUiScale('comfortable'); }
          if (!localStorage.getItem('ds_line_spacing')) { changeLineSpacing('relaxed');   }
          if (!localStorage.getItem('ds_button_scale')) { changeButtonScale('large');     }
          if (!localStorage.getItem('ds_font_size'))    { changeFontSize('large');        }
          localStorage.setItem('ds_elderly_defaults_applied', '1');
        }
      } catch { /* profile not ready yet — will be applied on next mount */ }
    })();
  }, [changeFontSize, changeUiScale, changeLineSpacing, changeButtonScale]);

  // ── Global challenge auto-progress — fires autoCheckIn on every page ──────
  // Watches all activity stores and auto-checks active challenges when the
  // user does relevant work anywhere in the app (Bible, Prayer, Journal, etc.)
  useGlobalChallengeSync();
  useAutoChallengeProgress();
  // Imports prayer requests submitted through the public /pray/<token> link.
  usePrayerInboxImport();
  usePrayerAcks();
  useSharedPrayerSync();

  // Layout only needs dailyEntries and calendarEvents from the journal data set.
  // Previously called useJournalData() which subscribes 6 keys — 4 unused here —
  // opening 4 wasted Firestore onSnapshot listeners. calendarEvents was also
  // subscribed separately below, creating a duplicate listener for the same key.
  // Fix: subscribe only the 2 keys Layout actually reads.
  const [dailyEntries]   = useSyncStore(STORE_DAILY_ENTRIES,   {});
  const [calendarEvents] = useSyncStore(STORE_CALENDAR_EVENTS, []);
  const [challenges]     = useSyncStore(STORE_CHALLENGES,      []);
  // FIX #13: was useLocalStorage — misleading name + wrong data layer.
  // useSyncStore writes through IDB → Firestore, so these values are now
  // consistent with PrayerJournal (which writes via useSyncStore) and survive
  // sign-out/sign-in without leaking stale data from window.localStorage.
  const [prayerRequests] = useSyncStore(STORE_PRAYER_REQUESTS,  []);
  const [prayedDays]     = useSyncStore(STORE_PRAYER_CHECKINS,  {});
  const [sermonNotes]    = useSyncStore(STORE_SERMON_NOTES,    []);

  const filledDays = useMemo(() => {
    const map = {};
    // Days with a dailyEntry that has activity
    Object.entries(dailyEntries || {}).forEach(([k, e]) => {
      if (checkDayFilled(e, k, challenges, prayerRequests, prayedDays)) map[k] = true;
    });
    // Challenge check-ins may exist on days with no dailyEntry at all
    (challenges || []).forEach(c => {
      (c.checkIns || []).forEach(d => { map[d] = true; });
    });
    // Prayer requests added/answered on days with no dailyEntry
    (prayerRequests || []).forEach(r => {
      if (r.dateAdded)    map[r.dateAdded]    = true;
      if (r.dateAnswered) map[r.dateAnswered] = true;
    });
    // Prayed-today check-ins
    Object.entries(prayedDays || {}).forEach(([d, checkins]) => {
      if (checkins && Object.keys(checkins).length > 0) map[d] = true;
    });
    // Sermon notes saved on a specific date
    (sermonNotes || []).forEach(n => {
      if (n.date) map[n.date] = true;
    });
    // Calendar events (user-created, not auto-generated milestones)
    (calendarEvents || []).forEach(ev => {
      if (ev.date && !ev.isMilestone) map[ev.date] = true;
    });
    return map;
  }, [dailyEntries, challenges, prayerRequests, prayedDays, sermonNotes, calendarEvents]);

  const journalStreak = useMemo(
    () => consecutiveRun(dailyEntries, '0000-00-00', fmtDateLocal(new Date()), 0).length,
    [dailyEntries]
  );

  const [journalDate,  setJournalDate]  = useState(new Date());
  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [hideJournalDateBar, setHideJournalDateBar] = useState(false);
  const [profileOpen,  setProfileOpen]  = useState(false);
  const [levelOpen,    setLevelOpen]    = useState(false);
  const [drawerOpen,   setDrawerOpen]   = useState(false);

  // Close picker when navigating away from journal
  useEffect(() => {
    if (!isJournalPage) setPickerOpen(false);
  }, [location.pathname, isJournalPage]);

  // Reset date-bar visibility when navigating away from journal — otherwise
  // leaving while the Prayer & Praise tab is active would leave the date bar
  // permanently hidden on return.
  useEffect(() => {
    if (!isJournalPage) setHideJournalDateBar(false);
  }, [location.pathname, isJournalPage]);

  // Close all modals on navigation
  useEffect(() => {
    setDrawerOpen(false);
    setProfileOpen(false);
    setLevelOpen(false);
  }, [location.pathname]);

  // Double-back to exit — using beforeunload style (no history manipulation)
  const exitStepRef  = useRef(0);
  const exitTimerRef = useRef(null);
  const [exitMsg, setExitMsg] = useState('');

  const resetExit = useCallback(() => { exitStepRef.current = 0; setExitMsg(''); }, []);

  useEffect(() => {
    resetExit();
  }, [location.pathname, resetExit]);

  // Handle Android back button via popstate — ONLY intercept on root pages
  // and ONLY to show "press back again" warning. Never manipulate history.
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;

      // Handle custom back destinations (sub-routes)
      const customDest = CUSTOM_BACK[path];
      if (customDest) { navigate(customDest); return; }

      // On root pages, intercept to warn before exit
      if (ROOT_PAGES.includes(path)) {
        clearTimeout(exitTimerRef.current);
        const step = exitStepRef.current;
        if (step === 0) {
          exitStepRef.current = 1;
          setExitMsg('Press back again to exit');
          exitTimerRef.current = setTimeout(resetExit, 4000);
          // Push a dummy entry so the next back press fires popstate again
          window.history.pushState(null, '');
        } else if (step === 1) {
          exitStepRef.current = 2;
          setExitMsg('Press back once more to exit');
          exitTimerRef.current = setTimeout(resetExit, 3000);
          window.history.pushState(null, '');
        } else {
          resetExit();
          window.location.href = 'about:blank';
        }
      }
    };

    window.addEventListener('popstate', onPop);
    return () => { window.removeEventListener('popstate', onPop); clearTimeout(exitTimerRef.current); };
  }, [navigate, resetExit]);

  // Blur keyboard on tap-outside
  useEffect(() => {
    let justDismissed = false;
    const onDown = (e) => {
      const active = document.activeElement;
      if (!active || (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA')) return;
      if (e.target.closest('button,input,textarea,select,a,label,[role="button"],[role="option"]')) return;
      active.blur(); justDismissed = true; setTimeout(() => { justDismissed = false; }, 400);
    };
    const onClick = (e) => {
      if (!justDismissed) return; justDismissed = false;
      if (e.target.closest('nav,a[href],[data-testid="bottom-navigation"]')) return;
      e.stopImmediatePropagation();
    };
    document.addEventListener('pointerdown', onDown, { capture: true });
    document.addEventListener('click', onClick, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', onDown, { capture: true });
      document.removeEventListener('click', onClick, { capture: true });
    };
  }, []);

  const outletContext = { journalDate, setJournalDate, pickerOpen, setPickerOpen, setHideJournalDateBar, openLevelPanel: () => setLevelOpen(true), mainScrollRef };

  const bgStyle = isForestTheme
    ? { background: 'linear-gradient(160deg,#14532d 0%,#1c1917 60%,#292524 100%)', minHeight: '100vh' }
    : { minHeight: '100vh' };
  const bgClass = isForestTheme
    ? 'transition-colors'
    : 'bg-[#eeeeec] dark:bg-stone-900 transition-colors';

  return (
    <div className={bgClass} style={bgStyle}>
      {/* Exit warning toast */}
      {exitMsg && (
        <div className="fixed left-1/2 z-[200] px-5 py-2.5 rounded-full bg-stone-900/90 text-white text-sm font-medium shadow-xl backdrop-blur-sm"
          style={{ bottom: `calc(${NAV_H + 12}px + env(safe-area-inset-bottom,0px))`, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
          {exitMsg}
        </div>
      )}

      {/* Modals */}
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} avatarProfile={avatarProfile} onLevelClick={() => { setProfileOpen(false); setLevelOpen(true); }} />
      <LevelPanel   open={levelOpen}   onClose={() => setLevelOpen(false)} />
      <NavDrawer    open={drawerOpen}  onClose={() => setDrawerOpen(false)} onLevelClick={() => { setDrawerOpen(false); setLevelOpen(true); }} />

      {isTablet ? (
        <>
          <SideNav onProfileOpen={() => setProfileOpen(true)} photo={avatarProfile.photo} onLevelClick={() => setLevelOpen(true)} />
          <main className="ml-56 h-screen w-[calc(100%-14rem)]">
            {isJournalPage && !hideJournalDateBar && (
              <div className="sticky top-0 z-40">
                <JournalDateBar journalDate={journalDate} setJournalDate={setJournalDate} pickerOpen={pickerOpen} setPickerOpen={setPickerOpen} filledDays={filledDays} streakCount={journalStreak} isTablet={true} />
              </div>
            )}
            <div className={`w-full max-w-5xl mx-auto px-6 py-8 lg:px-10 lg:py-10 ${location.pathname === '/' ? 'h-full' : ''}`} style={{ zoom: 'clamp(0.78,0.65 + 0.18vw,1)' }}>
              <Outlet context={outletContext} />
            </div>
          </main>
        </>
      ) : (
        <>
          {/* Mobile top header */}
          {/* iOS fix: paddingTop pushes content below the status bar (safe-area-inset-top ≈ 44-59px on notch/island devices) */}
          <div className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-stone-900/90 backdrop-blur-xl border-b border-stone-200 dark:border-stone-700"
            style={{ paddingTop: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))', transform: 'translateZ(0)', willChange: 'transform' }}>
            {/* ── Mobile top header ──────────────────────────────────────────
                 Layout: [avatar + "Sheepheard"] [tagline fills remaining space]
                 Both pieces scale with the user's font-size setting:
                 • Title uses `1em` so it inherits root font size.
                 • Tagline uses clamp(min, fluid-vw, max) so it fills the gap
                   without truncating on small screens or wasting space when
                   the font setting is small.  `min-width:0` on the flex child
                   allows it to shrink below its content size (required for
                   text-overflow to work if it ever gets very long).
                ─────────────────────────────────────────────────────────────── */}
            <div className={`flex items-center gap-2 ${IS_IOS ? 'px-3 h-10' : 'px-4 h-12'}`}>
              <button
                onClick={() => setProfileOpen(true)}
                className="flex items-center gap-2 shrink-0 hover:opacity-70 active:scale-95 transition-all"
              >
                <Avatar size={IS_IOS ? 24 : 28} photo={avatarProfile.photo} />
                <p
                  className="font-serif font-bold text-stone-900 dark:text-stone-100 leading-tight whitespace-nowrap"
                  style={{ fontSize: 'clamp(0.8rem, 3.5vw, 0.9375rem)' }}
                >Sheepheard</p>
              </button>
              {/* Tagline: fluid font fills remaining space, always one line.
                  min-width:0 lets the flex child shrink below content width.
                  white-space:nowrap + text-overflow:ellipsis guarantee a
                  single-line layout at any font-size setting — the clamp
                  scales the font to fill available space and ellipsis only
                  fires on extremely narrow viewports (< ~320px). */}
              <p
                className="text-stone-500 italic leading-tight pointer-events-none select-none min-w-0 flex-1"
                style={{
                  fontSize:     'clamp(0.6rem, 2.6vw, 0.8rem)',
                  whiteSpace:   'nowrap',
                  overflow:     'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                "My sheep hears my voice, and I know them, and they follow me."
              </p>
              {/* Sync status dot — only visible when offline or pending */}
              <SyncStatusBadge />
            </div>
            {/* Offline / pending-sync banner — slim row below the title bar */}
            <OfflineBanner />
          </div>

          {/* iOS: paddingTop = h-12 (3rem) + status-bar inset that was added to the fixed header.
               minHeight 100dvh (dynamic) prevents layout shift when Safari chrome shows/hides. */}
          <div
            className="fixed left-0 right-0 z-40 flex items-center justify-center pointer-events-none transition-opacity"
            style={{
              top: IS_IOS
                ? 'calc(2.5rem + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))'
                : 'calc(3rem + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))',
              height: `${pullDistance}px`,
              opacity: pullDistance > 0 ? 1 : 0,
            }}
          >
            <div className="rounded-full border-2 border-forest-500 border-t-transparent animate-spin" style={{ width: 20, height: 20 }} />
          </div>
          <main ref={mainScrollRef} style={{
            paddingTop: IS_IOS
              ? 'calc(2.5rem + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))'
              : 'calc(3rem + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))',
            paddingBottom: isJournalPage
              ? `calc(${NAV_H + BAR_H}px + env(safe-area-inset-bottom,0px) + ${IS_IOS ? '0.5rem' : '1rem'})`
              : `calc(${NAV_H}px + env(safe-area-inset-bottom,0px))`,
            height: '100dvh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorY: 'none',
          }}>
            <div className={`max-w-xl mx-auto px-4 sm:px-6 pt-0 pb-0 ${location.pathname === '/' ? 'h-full' : ''}`}>
              <Outlet context={outletContext} />
            </div>
          </main>

          <BottomNav onLevelClick={() => setLevelOpen(true)} />

          {isJournalPage && !hideJournalDateBar && (
            <JournalDateBar journalDate={journalDate} setJournalDate={setJournalDate} pickerOpen={pickerOpen} setPickerOpen={setPickerOpen} filledDays={filledDays} streakCount={journalStreak} scrollContainerRef={mainScrollRef} />
          )}

          <UpdateBanner />
        </>
      )}
    </div>
  );
};

export default Layout;